
import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { bodyContentMapper } from "components/studio/board/nodes/Node.lib";
import { renderHTML } from "components/util/render_util";
import l from "components/util/l";
import HelpMessage from "components/util/helpers/HelpMessage";
import ChatSuggestions from "components/presentation/chat/Chat.sub.suggestions.view";

const ChatMessage = ({
  id,
  models,
  chat,
  processCount,
  collectionCount,
  index,
  type,
  content,
  action,
  prevMessage,
  nextMessage,
  suggestions,
  currentMessage,
  messageSettings,
  chatSettings,
  selectedNote,
  removePadding,
  showModel,
  showProcesses,
  isExecutionChat,
  highlightMessage,
  onClickRegenerateMessage,
  onClickRepeatExecution,
  onClickSuggestion,
}) => {

  const chatModel = chatSettings?.model ?? 'OpenAI GPT-4o mini';
  const messageModel = messageSettings?.llm_model ?? chatModel;

  const text = content.text;
  const plain = content.plain;
  const attachments = content.attachments;

  const messageRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [dropdownData, setDropdownData] = useState(null);
  const [isSuggestionShown, setIsSuggestionShown] = useState(false);

  const [suggestionPage, setSuggestionPage] = useState(0);
  const suggestionPageSize = 4;

  const createProcessSuggestion = {
    suggestion: {
      action: { node: { root_settings: { icon: "fas fa-screwdriver-wrench" } } },
      processName: "CREATE_PROCESS",
      content: { text: l("Crear proceso%PA131") }
    },
    type: "WIZARD"
  };

  suggestions = suggestions.sort((a, b) => a.id - b.id);
  suggestions = suggestions.map((s) => ({
    suggestion: s,
    type: "NODE"
  }));

  const hasNodeSuggestions = suggestions.length > 0;

  if (processCount == 0 && index == 0 && showProcesses) 
    suggestions = [...suggestions, createProcessSuggestion];

  const pagedSuggestions = suggestions.reduce((acc, curr, idx) => {
    const pageIndex = Math.floor(idx / suggestionPageSize);
    if (!acc[pageIndex]) acc[pageIndex] = [];
    acc[pageIndex].push(curr);
    return acc;
  }, []);


  let suggestionTotalPages = pagedSuggestions.length;
  let currentPageSuggestions = pagedSuggestions[suggestionPage] ?? [];

  const onClickCopy = (e) => {

    navigator.clipboard.writeText(plain || text);
    setHasCopied(true);

    const y = (e.clientY || e.pageY);
    const x = (e.clientX || e.pageX) + (type == "BOT_MESSAGE" ? -125 : 20);

    const messageDiv = document.createElement('div');
    messageDiv.innerText = l("Texto copiado%PA009");
    messageDiv.style.position = 'fixed';
    messageDiv.style.top = `${y}px`;
    messageDiv.style.left = `${x}px`;
    messageDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    messageDiv.style.color = 'white';
    messageDiv.style.padding = '5px 10px';

    document.body.appendChild(messageDiv);
    setTimeout(() => {
      document.body.removeChild(messageDiv);
    }, 1200);

  }

  const onClickOpenRegenerateOptions = (e) => {

    e.preventDefault();
    e.stopPropagation();

    let x = (e.clientX || e.pageX) + 20;
    let dropdownHeight = models.length * 45;

    let y = (e.clientY || e.pageY);
    if (y + dropdownHeight > window.innerHeight)
      y = window.innerHeight - dropdownHeight - 10;

    setDropdownData({ x, y });

  }

  const onClickRegenerate = (message, model) => {
    setDropdownData(null);
    onClickRegenerateMessage(message, model);
  }

  const onClickMoreSuggestions = () =>
    setSuggestionPage((suggestionPage + 1) % suggestionTotalPages);

  useEffect(() => {
    setTimeout(() => {
      let variables = document.getElementsByClassName("variable")
      for (let variable of variables)
        try {
          variable.textContent = variable.getAttribute("data-id")
        } catch { }
    }, 100);
  }, [])

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [hasCopied]);

  useEffect(() => {
    if (selectedNote?.message?.id == id && id != null)
      setIsFocused(true);
  }, [selectedNote, id]);

  useEffect(() => {
    if (isFocused) {
      setTimeout(() => {
        setIsFocused(false);
      }, 2000);
    }
  }, [isFocused]);

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        setDropdownData(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);

  }, [messageRef]);

  return (<>

    <div
      id={`chat-message-${id}`}
      className="w-100"
      style={{
        backgroundColor: isFocused ? "rgba(0, 0, 0, 0.1)" : "transparent",
      }}
    >
      <div
        className={
          `message-wrapper d-flex ${type == "BOT_MESSAGE" ? "flex-row" : "flex-row-reverse user"} w-auto animate__animated animate__fadeIn animate__faster 
          mb-1 position-relative w-100 ${removePadding ? "px-0 mx-0" : "mx-2"}`
        }>

        <div
          className={`message ${type == "BOT_MESSAGE" ? "" : "user"}
            ${type == "BOT_MESSAGE" ? "order-1" : "order-2"} ${highlightMessage ? "highlighted-message" : ""}`}
          style={{
            maxWidth: window.innerWidth > 576 ?
              (plain?.length > 120 ? (4 * Math.log(plain?.length - 120) + 66) + "%" : "66%") :
              (plain?.length > 120 ? (4 * Math.log(plain?.length - 120) + 75) + "%" : "75%"),
            marginTop: window.innerWidth > 576 ?
              (prevMessage?.type != type ? "0.7rem" : "0.2rem") :
              (prevMessage?.type != type ? "0.4rem" : "0.1rem"),
            marginBottom: window.innerWidth > 576 ?
              (nextMessage?.type != type ? "0.7rem" : "0.2rem") :
              (nextMessage?.type != type ? "0.4rem" : "0.1rem"),
            zIndex: 1,
          }}>

          {
            text.includes("@PA") ? l(text.replace("<p>", "").replace("</p>", "")) :
              renderHTML(
                action?.node && action?.node?.action?.type == "TRIGGER" && !action?.is_form
                  ? bodyContentMapper(action.node?.action?.ui_settings?.node, action?.node?.input_values,
                    "execution_message", action.node?.action?.ui_settings?.form)
                  : text)
          }

          {
            attachments && attachments.length > 0 &&
            <div className="d-flex flex-column">
              {
                attachments.map((attachment, index) => {
                  return (
                    <div
                      key={`attachment-${index}`}
                      className="d-flex align-items-center btn btn-lighter-white mb-2 text-truncate"
                      role="button"
                      onClick={() => window.open(attachment.data.url, "_blank")}
                    >
                      <i className="fas fa-paperclip mr-2" />
                      {attachment.data.name?.substring(0, 35)}...
                    </div>
                  );
                })
              }
            </div>
          }

          {!isExecutionChat &&
            <div>
              {
                messageSettings?.hide_suggestions && !isSuggestionShown && hasNodeSuggestions &&
                <div className="message-suggestions">
                  <div
                    className="more-options-btn mt-n2"
                    onClick={() => setIsSuggestionShown(true)}>
                    <a className="fas fa-ellipsis-h mr-2"></a>{l('Ver procesos%PA172')}
                  </div>
                </div>
              }

              {((messageSettings?.hide_suggestions && isSuggestionShown && hasNodeSuggestions) ||
                (!messageSettings?.hide_suggestions && index == 0)) &&
                <ChatSuggestions
                  suggestions={currentPageSuggestions}
                  currentMessage={currentMessage}
                  suggestionTotalPages={suggestionTotalPages}
                  onClickSuggestion={onClickSuggestion}
                  onClickMoreSuggestions={onClickMoreSuggestions}
                />
              }
            </div>
          }


        </div>

        {
          function () {
            const showRegenerateOption = (messageSettings?.is_open_response ?? false) || (messageSettings?.is_context_response ?? false)
            const showRepeateOption = messageSettings?.is_execution_response ?? false

            return (
              <div className={`d-none d-md-block ${type == "BOT_MESSAGE" ? "order-2" : "order-1"}`}>
                <div className={`message-options d-flex align-items-start h-auto
                ${plain.length < 120 && (showRegenerateOption || showRepeateOption) ? "flex-wrap" : "flex-column"}`}>

                  <HelpMessage
                    message="Copiar mensaje"
                    placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"} >
                    <button
                      type="button"
                      className="btn btn-link p-0 mb-2 mr-2"
                      onClick={onClickCopy}>
                      {
                        hasCopied
                          ? <i className="fas fa-lg fa-check" />
                          : <i className="fas fa-lg fa-copy" />
                      }
                    </button>
                  </HelpMessage>

                  <HelpMessage
                    message="Guardar nota"
                    placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"} >
                    <Link
                      type="button"
                      className="btn btn-link p-0 mb-2 mr-2"
                      to={`/flow_notes/save/${currentMessage?.id}`} >
                      <i className="fas fa-lg fa-save" />
                    </Link>
                  </HelpMessage>

                  {showRegenerateOption &&
                    <HelpMessage
                      message={l("Volver a generar respuesta%PA008")}
                      placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"} >
                      <button
                        className="btn btn-link p-0 mb-2 mr-2"
                        onClick={onClickOpenRegenerateOptions}>
                        <i className="fas fa-lg fa-sync" />
                      </button>
                    </HelpMessage>}

                  {showRepeateOption &&
                    <HelpMessage
                      message="Repetir ejecución"
                      placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"}>
                      <button
                        className="btn btn-link p-0 mb-2 mr-2"
                        onClick={() => onClickRepeatExecution(id)} >
                        <i className="fas fa-lg fa-play" />
                      </button>
                    </HelpMessage>}

                </div>
              </div>
            )
          }()
        }

      </div>
    </div>

    {
      dropdownData &&
      <div
        ref={messageRef}
        class="dropdown-menu show"
        aria-labelledby="regenerate-options"
        style={{
          zIndex: 10,
          position: "fixed",
          top: `${dropdownData.y}px`,
          left: `${dropdownData.x}px`,
        }}
      >
        <h6 className="dropdown-header">{l("Modelo%PA007")}</h6>
        {
          models.map((model) =>
            model == messageModel ? null :
              <a href="#" className="dropdown-item" key={`model-${model}`} onClick={() => onClickRegenerate(currentMessage, model)}>
                <i className={`text-primary mr-2 ${getModelIcon(messageModel, model)}`} />
                {model}
              </a>
          )
        }
        <div className="dropdown-divider"></div>
        <a href="#" className="dropdown-item" onClick={() => onClickRegenerate(currentMessage, messageModel)}>
          <i className={`text-primary mr-2 ${getModelIcon(messageModel)}`} />
          {messageModel}
        </a>
      </div>
    }

  </>);

}

const getModelIcon = (defaultModel, model = null) => {
  if (!model) return "fas fa-sync";
  if (model.includes("OpenAI o")) return "fas fa-brain";
  if (model.includes("OpenAI GPT")) return "fas fa-sun";
  if (model.includes("DeepSeek")) return "fas fa-microchip";
  if (model.includes("Gemini")) return "fas fa-magic";
}

export default ChatMessage;